import { DateTime } from "luxon";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { RequestsSearchParamAccessors } from "@features/service-requests-feature/utils/constants";
import {
  DateFilterOption,
  ServiceRequestDateFilter,
  GetUniqueFilterValuesRequestBase,
  ServiceReportsFilterRequest,
  ServiceRequestSearchQuery,
  SortDirection,
} from "@lib/ShiOneClient";
import { SortingOrder } from "../../../../../components/mui-data-grid/constants";
import {
  FilterType,
  IFilters,
} from "shared-ui/src/interfaces/ToolbarInterfaces";

export const mapSortDirectionToEnum = (
  sortDirectionValue: string,
): SortDirection | undefined => {
  if (sortDirectionValue in SortDirection) {
    return SortDirection[sortDirectionValue as keyof typeof SortDirection];
  }
  return undefined;
};

export const getServiceRequestGridDefinitions = (gridDefinitions: any[]) => {
  const columnDefinitions = gridDefinitions;
  const pinnedColumns = { left: ["title"] };
  const hiddenColumns: string[] = ["id"];
  const sortedColumns: GridSortModel = [
    { field: "createdDate", sort: SortingOrder.Descending },
  ];
  return {
    columnDefinitions,
    pinnedColumns,
    hiddenColumns,
    sortedColumns,
  };
};

export const setTermFiltersForRequest = (
  filters: IFilters[],
  setRequestFilters: (requestFilters: Record<string, Array<string>>) => void,
) => {
  const requestFilters = filters.reduce(
    (filter: any, currentFilter: IFilters) => {
      if (
        currentFilter.value &&
        currentFilter.accessor &&
        currentFilter.filterType !== FilterType.DatePicker
      ) {
        filter[currentFilter.accessor] = Array.isArray(currentFilter.value)
          ? currentFilter.value
          : [currentFilter.value];
      }
      return filter;
    },
    {} as Record<string, string>,
  );

  setRequestFilters({ ...requestFilters });
};

export const setDateFiltersForRequest = (
  filters: IFilters[],
  setRequestFilters: (
    requestFilters: Record<string, ServiceRequestDateFilter>,
  ) => void,
) => {
  const requestFilters = filters.reduce(
    (filter: any, currentFilter: IFilters) => {
      if (
        currentFilter.accessor &&
        currentFilter.filterType === FilterType.DatePicker &&
        (currentFilter.startDate || currentFilter.endDate)
      ) {
        filter[currentFilter.accessor] = {
          startDate: currentFilter.startDate,
          endDate: currentFilter.endDate,
        };
      }
      return filter;
    },
    {} as Record<string, ServiceRequestDateFilter>,
  );
  setRequestFilters(requestFilters);
};

export const sortOptions = (options: any[]) => {
  return options.slice().sort((a, b) => a.localeCompare(b));
};

export function createUniqueFilterRequest(
  maxSize: number | undefined,
  serviceRequestQuery: ServiceReportsFilterRequest,
  sortBy: string,
  sortOrder: string,
) {
  const uniqueFilterValuesRequest = new GetUniqueFilterValuesRequestBase();
  uniqueFilterValuesRequest.query = new ServiceRequestSearchQuery();
  uniqueFilterValuesRequest.query.resultsFrom = 0;
  uniqueFilterValuesRequest.query.pageSize = maxSize;
  uniqueFilterValuesRequest.query.sortBy = sortBy;
  uniqueFilterValuesRequest.query.sortDirection =
    mapSortDirectionToEnum(sortOrder);
  uniqueFilterValuesRequest.serviceRequestQueryType =
    serviceRequestQuery.queryType;

  return uniqueFilterValuesRequest;
}

export function getDateRangeForFilter(option: string): {
  startDate: DateTime;
  endDate: DateTime;
} {
  const now = DateTime.now();

  switch (option) {
    case DateFilterOption.CurrentMonth: {
      const startDate = now.startOf("month");
      const endDate = now.endOf("month");
      return { startDate, endDate };
    }
    case DateFilterOption.LastMonth: {
      const lastMonth = now.minus({ months: 1 });
      const startDate = lastMonth.startOf("month");
      const endDate = lastMonth.endOf("month");
      return { startDate, endDate };
    }
    case DateFilterOption.CurrentQuarter: {
      const startDate = now.startOf("quarter");
      const endDate = now.endOf("quarter");
      return { startDate, endDate };
    }
    case DateFilterOption.LastQuarter: {
      const lastQuarter = now.minus({ quarters: 1 });
      const startDate = lastQuarter.startOf("quarter");
      const endDate = lastQuarter.endOf("quarter");
      return { startDate, endDate };
    }
    case DateFilterOption.YearToDate: {
      const startDate = now.startOf("year");
      const endDate = now.endOf("day");
      return { startDate, endDate };
    }
    case DateFilterOption.LastYear: {
      const lastYear = now.minus({ years: 1 });
      const startDate = lastYear.startOf("year");
      const endDate = lastYear.endOf("year");
      return { startDate, endDate };
    }
    default:
      return {
        startDate: now.startOf("month"),
        endDate: now.endOf("month"),
      };
  }
}

export const mergeInitialFiltersWithCreatedDateRangeSearchQuery = (
  initialFilters: IFilters[],
  urlSearchParams: Record<string, string>,
): IFilters[] => {
  return initialFilters.map((filter) => {
    if (
      filter.filterType === FilterType.DatePicker &&
      urlSearchParams[RequestsSearchParamAccessors.createdDate] &&
      filter.accessor === RequestsSearchParamAccessors.createdDate
    ) {
      const { startDate, endDate } = getDateRangeForFilter(
        urlSearchParams[RequestsSearchParamAccessors.createdDate],
      );

      return {
        ...filter,
        startDate,
        endDate,
      };
    }

    return {
      ...filter,
      value: urlSearchParams[filter.accessor] ?? filter.value,
    };
  });
};

export const parseTrackingNumberFromDeviceReceived = (
  deviceReceived?: string,
): string | null => {
  if (!deviceReceived) return null;
  const match = deviceReceived.match(/\[Tracking #: ([^\]]+)\]/);
  return match ? match[1] : null;
};

export function parseDateFromDeviceReceived(
  deviceReceived?: string,
): string | null {
  if (!deviceReceived) return null;
  const match = deviceReceived.match(/^(\d{1,2}\/\d{1,2}\/\d{4})/);
  return match ? match[1] : null;
}
